import { useState, useEffect } from "react";
import { Star, X } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

interface Review {
  id: string;
  author_name: string;
  rating: number;
  review_text: string | null;
}

const ReviewCarousel = ({ isDark = false }: { isDark?: boolean }) => {
  const [reviews, setReviews] = useState<Review[]>([]);
  const [selectedReview, setSelectedReview] = useState<Review | null>(null);

  useEffect(() => {
    const fetchReviews = async () => {
      const { data } = await supabase
        .from("google_reviews")
        .select("id, author_name, rating, review_text")
        .eq("is_featured", true)
        .order("created_at", { ascending: false });
      if (data) setReviews(data);
    };
    fetchReviews();
  }, []);

  if (reviews.length === 0) return null;

  const cardBg = isDark
    ? "bg-[#161616] shadow-[3px_3px_8px_#000,-3px_-3px_8px_#1a1a1a]"
    : "bg-[#f5f5f5] shadow-[3px_3px_8px_#d8d8d8,-3px_-3px_8px_#fefefe]";
  const textColor = isDark ? "text-gray-300" : "text-gray-600";
  const nameColor = isDark ? "text-white" : "text-[#1C2333]";
  const modalBg = isDark ? "bg-[#1a1a1a]" : "bg-white";

  const items = [...reviews, ...reviews];

  const truncate = (text: string, len = 120) =>
    text.length > len ? text.slice(0, len) + "…" : text;

  return (
    <>
      <div className="w-full py-3 md:py-4 overflow-hidden">
        <h3 className="text-center text-sm mb-2 md:mb-3 md:text-4xl font-normal" style={{ color: "#E4683B" }}>
          What Our Clients Say
        </h3>
        <div className="relative overflow-hidden">
          <div className="flex gap-3 md:gap-4 animate-scroll-left w-max px-4">
            {items.map((review, i) => (
              <div
                key={`${review.id}-${i}`}
                onClick={() => setSelectedReview(review)}
                className={`flex-shrink-0 w-[calc(100vw/3-16px)] min-w-[200px] max-w-[420px] rounded-xl p-3 md:p-5 ${cardBg} flex flex-col cursor-pointer hover:scale-[1.02] transition-transform duration-200`}
              >
                <div className="flex items-center gap-2 mb-2">
                  <div
                    className="w-7 h-7 md:w-9 md:h-9 rounded-full flex items-center justify-center text-white text-xs md:text-sm font-bold flex-shrink-0"
                    style={{ background: "#E4683B" }}
                  >
                    {review.author_name.charAt(0)}
                  </div>
                  <span className={`md:text-sm font-semibold ${nameColor} truncate text-sm`}>{review.author_name}</span>
                  <div className="flex gap-0.5 ml-auto flex-shrink-0">
                    {[1, 2, 3, 4, 5].map(s => (
                      <Star key={s} className="w-3 h-3 md:w-4 md:h-4" fill={s <= review.rating ? "#facc15" : "none"} stroke="#facc15" />
                    ))}
                  </div>
                </div>
                <p className={`md:text-sm leading-relaxed ${textColor} line-clamp-3 text-sm`}>
                  {truncate(review.review_text || "")}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Review Popup Modal */}
      {selectedReview && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={() => setSelectedReview(null)}
        >
          <div
            className={`relative w-full max-w-lg rounded-2xl p-6 md:p-8 ${modalBg} shadow-2xl`}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setSelectedReview(null)}
              className="absolute top-3 right-3 p-1.5 rounded-full hover:bg-gray-200/50 transition-colors"
            >
              <X className="w-5 h-5" style={{ color: isDark ? "#fff" : "#333" }} />
            </button>
            <div className="flex items-center gap-3 mb-4">
              <div
                className="w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-bold"
                style={{ background: "#E4683B" }}
              >
                {selectedReview.author_name.charAt(0)}
              </div>
              <div>
                <p className={`text-base font-semibold ${nameColor}`}>{selectedReview.author_name}</p>
                <div className="flex gap-0.5">
                  {[1, 2, 3, 4, 5].map(s => (
                    <Star key={s} className="w-4 h-4" fill={s <= selectedReview.rating ? "#facc15" : "none"} stroke="#facc15" />
                  ))}
                </div>
              </div>
            </div>
            <p className={`text-sm md:text-base leading-relaxed ${textColor}`}>
              {selectedReview.review_text}
            </p>
          </div>
        </div>
      )}
    </>
  );
};

export default ReviewCarousel;
